import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const PregnancyCalculatorPage = () => {
  const [calcMethod, setCalcMethod] = useState('lmp');
  const [lmpDate, setLmpDate] = useState('');
  const [conceptionDate, setConceptionDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [cycleLength, setCycleLength] = useState('28');
  const [result, setResult] = useState(null);

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const calculate = () => {
    let estimatedDueDate, estimatedConception, startDate;
    const today = new Date();
    const cycle = parseInt(cycleLength) || 28;
    const ovulationDay = cycle - 14;

    if (calcMethod === 'lmp' && lmpDate) {
      startDate = new Date(lmpDate);
      estimatedConception = addDays(startDate, ovulationDay);
      estimatedDueDate = addDays(startDate, 280);
    } else if (calcMethod === 'conception' && conceptionDate) {
      estimatedConception = new Date(conceptionDate);
      startDate = addDays(estimatedConception, -ovulationDay);
      estimatedDueDate = addDays(estimatedConception, 266);
    } else if (calcMethod === 'due' && dueDate) {
      estimatedDueDate = new Date(dueDate);
      startDate = addDays(estimatedDueDate, -280);
      estimatedConception = addDays(startDate, ovulationDay);
    } else {
      return;
    }

    const pregnancyDays = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
    const weeksPregnant = Math.floor(pregnancyDays / 7);
    const daysExtra = pregnancyDays % 7;
    const daysRemaining = Math.floor((estimatedDueDate - today) / (1000 * 60 * 60 * 24));
    const trimester = weeksPregnant < 13 ? 1 : weeksPregnant < 27 ? 2 : 3;
    const progress = Math.min(100, Math.max(0, (pregnancyDays / 280) * 100));

    // Key milestones
    const milestones = [
      { week: 4, name: 'Missed Period', date: addDays(startDate, 28) },
      { week: 8, name: 'First Prenatal Visit', date: addDays(startDate, 56) },
      { week: 12, name: 'End of First Trimester', date: addDays(startDate, 84) },
      { week: 20, name: 'Anatomy Scan', date: addDays(startDate, 140) },
      { week: 27, name: 'End of Second Trimester', date: addDays(startDate, 189) },
      { week: 37, name: 'Full Term', date: addDays(startDate, 259) },
      { week: 40, name: 'Due Date', date: estimatedDueDate }
    ];

    setResult({
      dueDate: estimatedDueDate,
      conception: estimatedConception,
      lmp: startDate,
      weeksPregnant,
      daysExtra,
      daysRemaining,
      trimester,
      progress,
      milestones
    });
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Pregnancy Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Pregnancy Calculator</h1>
          <p className="text-gray-400 mb-6">Calculate your due date and track your pregnancy progress.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex flex-wrap border-b border-[#2a2a2a] mb-6 gap-1">
              {[{ id: 'lmp', label: 'Last Period' }, { id: 'conception', label: 'Conception Date' }, { id: 'due', label: 'Due Date' }].map(m => (
                <button key={m.id} onClick={() => setCalcMethod(m.id)}
                  className={`px-4 py-2 font-medium text-sm border-b-2 ${calcMethod === m.id ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                  {m.label}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {calcMethod === 'lmp' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-40">Last Menstrual Period</label>
                  <Input type="date" value={lmpDate} onChange={(e) => setLmpDate(e.target.value)}
                    className="w-48 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              )}
              {calcMethod === 'conception' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-40">Conception Date</label>
                  <Input type="date" value={conceptionDate} onChange={(e) => setConceptionDate(e.target.value)}
                    className="w-48 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              )}
              {calcMethod === 'due' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-40">Due Date</label>
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                    className="w-48 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-40">Cycle Length</label>
                <Input type="number" value={cycleLength} onChange={(e) => setCycleLength(e.target.value)}
                  className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                <span className="text-gray-400">days</span>
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="text-center mb-6">
                  <p className="text-gray-400 mb-2">Estimated Due Date</p>
                  <p className="text-3xl font-bold text-green-400">{formatDate(result.dueDate)}</p>
                  <p className="text-cyan-400 mt-2">You are {result.weeksPregnant} weeks and {result.daysExtra} days pregnant</p>
                  <p className="text-gray-500">Trimester {result.trimester} • {result.daysRemaining > 0 ? `${result.daysRemaining} days remaining` : 'Due any day!'}</p>
                </div>

                <div className="mb-6">
                  <div className="flex justify-between text-sm text-gray-400 mb-1">
                    <span>Progress</span>
                    <span>{result.progress.toFixed(0)}%</span>
                  </div>
                  <div className="h-3 bg-[#2a2a2a] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-500 to-green-500 rounded-full transition-all"
                      style={{ width: `${result.progress}%` }}></div>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Conception Date</p>
                    <p className="text-white font-medium">{result.conception.toLocaleDateString()}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Last Period</p>
                    <p className="text-white font-medium">{result.lmp.toLocaleDateString()}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Due Date</p>
                    <p className="text-white font-medium">{result.dueDate.toLocaleDateString()}</p>
                  </div>
                </div>

                <h4 className="text-white font-semibold mb-3">Key Milestones</h4>
                <div className="space-y-2">
                  {result.milestones.map((m, i) => {
                    const isPast = m.date < new Date();
                    return (
                      <div key={i} className={`flex justify-between p-3 rounded-lg ${isPast ? 'bg-green-500/10 border border-green-500/30' : 'bg-[#1a1a1a]'}`}>
                        <span className={isPast ? 'text-green-400' : 'text-gray-400'}>
                          {isPast && '✓ '}{m.name} (Week {m.week})
                        </span>
                        <span className="text-white">{m.date.toLocaleDateString()}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <span className="text-gray-500">Related:</span>
            <Link to="/due-date-calculator" className="text-cyan-400 hover:text-cyan-300">Due Date Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/pregnancy-conception-calculator" className="text-cyan-400 hover:text-cyan-300">Conception Calculator</Link>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Fitness & Health Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'BMI', path: '/bmi-calculator' }, { name: 'Calorie', path: '/calorie-calculator' }, { name: 'Due Date', path: '/due-date-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PregnancyCalculatorPage;
